import React, { useEffect, useRef, useState } from 'react'
import { UnsavedChangesModal } from '../../../../common/components/UnsavedChangesModal'
import { customSwal } from '../../../../config/swal'
import { BackofficePermissionsEnum as Permissions } from '../../../../common/enums/backOfficePermissionsEnum'
import usePermissions from '../../../../common/hooks/usePermissions'
import { useUnsavedChanges } from '../../../../common/hooks/useUnsavedChanges'
import { FundDraftService, FundSeriesTab } from '../../../../services/FundDraftService'
import { TabSkeleton } from '../../../../common/components/TabSkeleton'
import { useFundSeries } from '../hooks/useFundSeries'

interface IStrategyItem {
  name: string
  color: string
  graphicValue: string
}

interface IProps {
  fundId: string
  onDirtyChange?: (dirty: boolean) => void
  saveRef?: React.MutableRefObject<(() => Promise<void>) | null>
}

const emptyItem: IStrategyItem = { name: '', color: 'var(--color-primary-blue)', graphicValue: '' }

export const StrategyTab: React.FC<IProps> = ({ fundId, onDirtyChange, saveRef }) => {
  const { hasPermission } = usePermissions()
  const { data: seriesData, isLoading, refetchSeriesOnly } = useFundSeries(fundId)

  const [activeSerieCode, setActiveSerieCode] = useState('')
  const [strategyAction, setStrategyAction] = useState<'saving' | 'publishing' | null>(null)
  const [visibility, setVisibility] = useState(true)
  const [graphicTitle, setGraphicTitle] = useState('')
  const [strategyTitle, setStrategyTitle] = useState('')
  const [items, setItems] = useState<IStrategyItem[]>([emptyItem])
  const [isDirty, setIsDirty] = useState(false)

  const initialized = useRef(false)
  const skipDirtyCount = useRef(1) // 1 for the mandatory mount fire

  const applySerieData = (code: string) => {
    if (!seriesData) return
    skipDirtyCount.current += 1 // +1 for the re-render caused by state updates below
    const strategy = seriesData.tabs.strategy[code]
    if (!strategy) {
      setItems([{ ...emptyItem }])
      setVisibility(true)
      setGraphicTitle('')
      setStrategyTitle('')
      return
    }
    if (strategy.data) setItems([...strategy.data])
    setVisibility(strategy.visibility)
    setGraphicTitle(strategy.graphicTitle ?? '')
    setStrategyTitle(strategy.title ?? '')
  }

  useEffect(() => {
    if (!seriesData) return
    if (initialized.current) {
      if (activeSerieCode) applySerieData(activeSerieCode)
      return
    }
    const firstCode = seriesData.series[0]?.serie_codigo
    if (!firstCode) return
    initialized.current = true
    setActiveSerieCode(firstCode)
    applySerieData(firstCode)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seriesData])

  const buildPayload = () => ({
    visibility,
    title: strategyTitle,
    graphicTitle,
    data: items,
  })

  const handleSave = async () => {
    setStrategyAction('saving')
    try {
      await FundDraftService().updateSeriesTab(
        fundId,
        activeSerieCode,
        FundSeriesTab.STRATEGY,
        buildPayload()
      )
      setIsDirty(false)
      refetchSeriesOnly()
      customSwal.fire({ title: 'Estrategia guardada correctamente', icon: 'success' })
    } catch {
      customSwal.fire({ title: 'Error al guardar la estrategia', icon: 'error' })
    }
    setStrategyAction(null)
  }

  const handlePublish = async () => {
    setStrategyAction('publishing')
    try {
      await FundDraftService().updateSeriesTab(
        fundId,
        activeSerieCode,
        FundSeriesTab.STRATEGY,
        buildPayload()
      )
      await FundDraftService().publishSeriesTab(fundId, activeSerieCode, FundSeriesTab.STRATEGY)
      setIsDirty(false)
      refetchSeriesOnly()
      customSwal.fire({ title: 'Estrategia publicada correctamente', icon: 'success' })
    } catch {
      customSwal.fire({ title: 'Error al publicar la estrategia', icon: 'error' })
    }
    setStrategyAction(null)
  }

  const { guardNavigation, isModalOpen, handleLeave, handleCancel, handleSaveAndContinue, saving } =
    useUnsavedChanges(isDirty, handleSave)

  const handleSerieChange = (code: string) => {
    guardNavigation(() => {
      setIsDirty(false)
      setActiveSerieCode(code)
      applySerieData(code)
    })
  }

  const addItem = (e: React.MouseEvent) => {
    e.preventDefault()
    setItems((prev) => [...prev, { ...emptyItem }])
  }

  const removeItem = (e: React.MouseEvent, index: number) => {
    e.preventDefault()
    setItems((prev) => prev.filter((_, i) => i !== index))
  }

  // Mark dirty when any user-editable state changes (skip load-triggered fires)
  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (skipDirtyCount.current > 0) { skipDirtyCount.current -= 1; return }
    setIsDirty(true)
  }, [visibility, graphicTitle, strategyTitle, items])

  // Report dirty state to ConfigFunds
  useEffect(() => {
    onDirtyChange?.(isDirty)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty])

  // Keep saveRef current
  useEffect(() => {
    if (saveRef) saveRef.current = handleSave
  })

  if (isLoading && !seriesData) return <TabSkeleton />

  const seriesTabs =
    seriesData && seriesData.series.length > 0 ? (
      <div
        style={{
          display: 'flex',
          borderBottom: '2px solid #f1f3f5',
          marginTop: '12px',
          marginLeft: '-20px',
          marginRight: '-20px',
          paddingLeft: '20px',
        }}
      >
        {seriesData.series.map((serie) => (
          <button
            key={serie.serie_codigo}
            type='button'
            onClick={() => handleSerieChange(serie.serie_codigo)}
            style={{
              border: 'none',
              background: 'none',
              padding: '6px 20px',
              cursor: 'pointer',
              fontWeight: activeSerieCode === serie.serie_codigo ? 600 : 400,
              color: activeSerieCode === serie.serie_codigo ? 'var(--color-primary-blue)' : '#9ca3af',
              borderBottom:
                activeSerieCode === serie.serie_codigo
                  ? '2px solid var(--color-primary-blue)'
                  : '2px solid transparent',
              marginBottom: '-2px',
              fontSize: '14px',
              transition: 'color 0.15s',
            }}
          >
            {serie.serie_name}
          </button>
        ))}
      </div>
    ) : null

  const edicionActivoBadge = seriesData ? (
    <span
      style={{
        background: '#d4edda',
        color: '#155724',
        fontSize: '12px',
        padding: '2px 10px',
        borderRadius: 20,
        fontWeight: 600,
      }}
    >
      Edición activo
    </span>
  ) : null

  return (
    <div>
      <div className='card mt-10 p-5'>
        <div className='d-flex align-items-center justify-content-between mt-2 mb-1'>
          <div className='d-flex align-items-center gap-3'>
            <span className='text-gray-800 fs-2 fw-bold'>Estrategia de inversión</span>
            {edicionActivoBadge}
          </div>
        </div>
        {seriesTabs}
      </div>

      {/* Mostrar información + Título de gráficos */}
      <div className='card mt-10 p-10'>
        <div className='d-flex align-items-center gap-3 mb-5'>
          <label className='fw-bolder fs-6 text-gray-700 mb-0'>Mostrar información</label>
          <div className='form-check form-switch m-0'>
            <input
              className='form-check-input'
              type='checkbox'
              role='switch'
              checked={visibility}
              onChange={(e) => setVisibility(e.target.checked)}
            />
          </div>
        </div>
        <div className='d-flex justify-content-between align-items-center mb-3'>
          <label className='fw-bolder fs-5 text-gray-700 mb-0'>Título de gráficos</label>
        </div>
        <textarea
          className='form-control'
          style={{ height: '50px', resize: 'none' }}
          required
          value={graphicTitle}
          onChange={(e) => setGraphicTitle(e.target.value)}
          placeholder='Título'
        />
      </div>

      {/* Título de estrategia */}
      <div className='card mt-10 p-10'>
        <label className='fw-bolder fs-5 text-gray-700 mb-3'>Título</label>
        <textarea
          className='form-control'
          style={{ height: '50px', resize: 'none' }}
          required
          value={strategyTitle}
          onChange={(e) => setStrategyTitle(e.target.value)}
          placeholder='Título'
        />
      </div>

      {/* Información del gráfico (tabla de items) */}
      <div className='card mt-10 p-10'>
        <div className='w-full mb-4 d-flex justify-content-between align-items-center'>
          <label className='fw-bolder fs-5 text-gray-700 mb-3'>Información del gráfico</label>
          <button
            style={{ color: 'white', marginRight: '2px', background: 'rgb(0, 123, 195)' }}
            className='btn mr-3'
            type='button'
            onClick={addItem}
            disabled={!visibility || !hasPermission(Permissions.FUND_TABS.STRATEGY.GRAPHIC_EDIT)}
          >
            <i style={{ color: 'white' }} className='bi bi-plus mr-2 fs-1' />
            <span>Agregar campo</span>
          </button>
        </div>
        <table className='table table-row-dashed table-row-gray-300 gy-7'>
          <thead>
            <tr className='fw-bolder fs-6 text-gray-700'>
              <th />
              <th>TÍTULO</th>
              <th>VALOR</th>
              <th>COLOR</th>
              <th className='text-center'>ACCIONES</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr key={index}>
                <td>
                  <div className='mt-5'>{index + 1}</div>
                </td>
                <td>
                  <input
                    className='form-control mb-2 mb-md-0'
                    placeholder='Ingrese título'
                    type='text'
                    value={item.name}
                    onChange={(e) => {
                      const updated = [...items]
                      updated[index] = { ...updated[index], name: e.target.value }
                      setItems(updated)
                    }}
                    disabled={!visibility || !hasPermission(Permissions.FUND_TABS.STRATEGY.GRAPHIC_EDIT)}
                  />
                </td>
                <td>
                  <input
                    type='number'
                    className='form-control mb-2 mb-md-0'
                    placeholder='Valor'
                    value={item.graphicValue}
                    onChange={(e) => {
                      const updated = [...items]
                      updated[index] = { ...updated[index], graphicValue: e.target.value }
                      setItems(updated)
                    }}
                    disabled={!visibility || !hasPermission(Permissions.FUND_TABS.STRATEGY.GRAPHIC_EDIT)}
                  />
                </td>
                <td>
                  <input
                    type='color'
                    style={{ height: '44px' }}
                    className='form-control'
                    value={item.color}
                    onChange={(e) => {
                      const updated = [...items]
                      updated[index] = { ...updated[index], color: e.target.value }
                      setItems(updated)
                    }}
                    disabled={!visibility || !hasPermission(Permissions.FUND_TABS.STRATEGY.GRAPHIC_EDIT)}
                  />
                </td>
                <td className='text-center'>
                  <button
                    disabled={!visibility || !hasPermission(Permissions.FUND_TABS.STRATEGY.GRAPHIC_EDIT)}
                    style={{ background: 'transparent', color: 'white', border: '1px solid #ccc' }}
                    className='btn btn-icon ml-3'
                    onClick={(e) => removeItem(e, index)}
                  >
                    <i className='bi bi-trash text-muted fs-3' />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && (
          <div className='text-end'>
            <button
              disabled={!visibility || !hasPermission(Permissions.FUND_TABS.STRATEGY.GRAPHIC_EDIT)}
              style={{
                background: 'var(--color-primary-blue)',
                color: 'white',
                marginRight: '2px',
              }}
              className='btn mr-3'
              onClick={addItem}
            >
              <i style={{ color: 'white' }} className='bi bi-plus fs-1' /> Agregar fila
            </button>
          </div>
        )}
      </div>

      {/* Acciones */}
      {activeSerieCode &&
        hasPermission([Permissions.FUND_TABS.STRATEGY.SAVE, Permissions.FUND_TABS.STRATEGY.PUBLISH]) && (
          <div className='card mt-5 p-5'>
            <div className='d-flex justify-content-end gap-3'>
              {hasPermission(Permissions.FUND_TABS.STRATEGY.SAVE) && (
                <button
                  type='button'
                  className='btn btn-custom-action'
                  onClick={handleSave}
                  disabled={strategyAction !== null}
                >
                  {strategyAction === 'saving' ? (
                    <span className='indicator-progress' style={{ display: 'block' }}>
                      Guardando...
                      <span className='spinner-border spinner-border-sm align-middle ms-2' />
                    </span>
                  ) : (
                    'Guardar cambios'
                  )}
                </button>
              )}
              {hasPermission(Permissions.FUND_TABS.STRATEGY.PUBLISH) && (
                <button
                  type='button'
                  className='btn btn-custom-action'
                  onClick={handlePublish}
                  disabled={strategyAction !== null}
                >
                  {strategyAction === 'publishing' ? (
                    <span className='indicator-progress' style={{ display: 'block' }}>
                      Publicando...
                      <span className='spinner-border spinner-border-sm align-middle ms-2' />
                    </span>
                  ) : (
                    'Publicar'
                  )}
                </button>
              )}
            </div>
          </div>
        )}

      {isModalOpen && (
        <UnsavedChangesModal
          onLeave={handleLeave}
          onCancel={handleCancel}
          onSave={handleSaveAndContinue}
          saving={saving}
        />
      )}
    </div>
  )
}
